import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, Icons, ToolButton, useModal, IconPresentationProvider } from '@ohif/ui-next';
import { useSystem } from '@ohif/core';
import HeaderReportInfo from './HeaderReportInfo';
import { preserveQueryParameters } from '@ohif/app';
import { Types } from '@ohif/core';
import { isPatientMode } from '../../../../platform/app/src/custom/utils/patientMode';
import { authService } from '../../../../platform/app/src/custom/auth/authService';
import ViewerRevampPatientBar from './ViewerRevampPatientBar';
import ImportStudyButton from './ImportStudy/ImportStudyButton';
import PatientLanguageToggle from './PatientLanguageToggle';
import ViewerResponsiveToolbar from './ViewerResponsiveToolbar';

function ViewerHeader({ appConfig }: withAppTypes<{ appConfig: AppTypes.Config }>) {
  const { servicesManager, extensionManager } = useSystem();
  const { customizationService } = servicesManager.services;

  const navigate = useNavigate();
  const location = useLocation();

  const onClickReturnButton = () => {
    const { pathname, search } = location;
    const currentParams = new URLSearchParams(search);
    const returnTo = currentParams.get('returnTo');

    // Prefer explicit return path (e.g. /peer-reviews). Only allow same-app relative paths.
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      navigate(returnTo);
      return;
    }

    const dataSourceIdx = pathname.indexOf('/', 1);

    const dataSourceName = pathname.substring(dataSourceIdx + 1);
    const existingDataSource = extensionManager.getDataSources(dataSourceName);

    const searchQuery = new URLSearchParams();
    if (dataSourceIdx !== -1 && existingDataSource) {
      searchQuery.append('datasources', pathname.substring(dataSourceIdx + 1));
    }
    preserveQueryParameters(searchQuery);

    navigate({
      pathname: '/',
      search: decodeURIComponent(searchQuery.toString()),
    });
  };

  const { t } = useTranslation();
  const { show } = useModal();

  const UserPreferencesModal = customizationService.getCustomization(
    'ohif.userPreferencesModal'
  ) as Types.MenuComponentCustomization;

  const openPreferencesModal = () =>
    show({
      content: UserPreferencesModal,
      title: UserPreferencesModal.title ?? t('UserPreferencesModal:User preferences'),
      containerClassName: UserPreferencesModal?.containerClassName ?? 'flex max-w-4xl p-6 flex-col',
      showCloseButton: false,
    });

  const patientMode = isPatientMode();
  const isReturnEnabled = !patientMode && !!appConfig.showStudyList;

  const handlePatientLogout = async () => {
    const searchParams = new URLSearchParams(location.search);
    const studyInstanceUIDs = searchParams.get('StudyInstanceUIDs');

    await authService.logout();

    if (studyInstanceUIDs) {
      navigate(`/patient/login?StudyInstanceUIDs=${studyInstanceUIDs}`);
    } else {
      navigate('/patient/login');
    }
  };

  /** Breathing room vs Figma [50-16198](https://www.figma.com/design/fUSHo5LGxrEfRYspiKqS4t/RIS-Reporting-flow?node-id=50-16198): wider gaps, taller toolbar band */
  const leftToolSections = [
    !patientMode
      ? {
          key: 'measurementTools',
          section: 'measurementTools',
        }
      : null,
    { key: 'navigationTools', section: 'navigationTools' },
    { key: 'layoutTools', section: 'layoutTools' },
    { key: 'viewManipulationTools', section: 'viewManipulationTools' },
    { key: 'probeDisplayTools', section: 'probeDisplayTools' },
  ].filter(Boolean) as { key: string; section: string }[];

  const toolbarActions = (
    <>
      <HeaderReportInfo
        servicesManager={servicesManager}
        appConfig={appConfig}
        extensionManager={extensionManager}
      />
      {!patientMode ? (
        <ToolButton
          id="viewer-user-preferences"
          icon="settings"
          size="small"
          label={UserPreferencesModal.menuTitle ?? t('Header:Preferences')}
          onInteraction={() => openPreferencesModal()}
        />
      ) : null}
      {patientMode ? (
        <Button
          variant="ghost"
          size="icon"
          className="text-viewer-accent hover:bg-viewer-chrome h-9 w-9 focus-visible:ring-2 focus-visible:ring-[#0891B2]/40"
          onClick={handlePatientLogout}
          aria-label={t('Header:Logout')}
          title={t('Header:Logout')}
        >
          <Icons.PowerOff aria-hidden />
        </Button>
      ) : null}
    </>
  );

  return (
    <IconPresentationProvider
      size="medium"
      IconContainer={ToolButton}
      tooltipTone="light"
    >
      <div
        className={
          patientMode
            ? 'viewer-chrome-light viewer-layout-revamp viewer-patient-mode flex min-h-0 flex-shrink-0 flex-col bg-white'
            : 'viewer-chrome-light viewer-layout-revamp flex min-h-0 flex-shrink-0 flex-col bg-white'
        }
      >
        <ViewerRevampPatientBar
          servicesManager={servicesManager}
          isReturnEnabled={isReturnEnabled}
          onBack={onClickReturnButton}
          trailing={
            patientMode ? (
              <>
                <PatientLanguageToggle />
                <ImportStudyButton />
                <HeaderReportInfo
                  servicesManager={servicesManager}
                  appConfig={appConfig}
                  extensionManager={extensionManager}
                />
                <ToolButton
                  id="viewer-user-preferences"
                  icon="settings"
                  size="small"
                  label={UserPreferencesModal.menuTitle ?? t('Header:Preferences')}
                  onInteraction={() => openPreferencesModal()}
                  className="viewer-ris-patient-bar__icon-btn"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="viewer-ris-patient-bar__icon-btn text-viewer-accent hover:bg-viewer-chrome h-9 w-9 shrink-0 focus-visible:ring-2 focus-visible:ring-[#0891B2]/40"
                  onClick={handlePatientLogout}
                  aria-label={t('Header:Logout')}
                  title={t('Header:Logout')}
                >
                  <Icons.PowerOff aria-hidden />
                </Button>
              </>
            ) : undefined
          }
        />
        {!patientMode ? (
          <div className="viewer-ris-toolbar-wrap viewer-ris-header-band flex items-center border-b border-[#e5e5e5] bg-white px-3 sm:px-5">
            <ViewerResponsiveToolbar
              patientMode={patientMode}
              leftToolSections={leftToolSections}
              actions={toolbarActions}
            />
          </div>
        ) : null}
      </div>
    </IconPresentationProvider>
  );
}

export default ViewerHeader;
